import type {Action, Store} from "redux";
import type {ThunkDispatch} from "redux-thunk";
import type {AppState} from "./rootReducer";

/**
 * Lazy accessor for the configured Redux store.
 *
 * This module exists to break the circular dependency chain:
 *   <slice-store-file> -> redux/store -> rootReducer -> <slice-store-file>
 *
 * Slice "store facade" files (e.g. mySongsPage.store.ts) used to import the
 * Redux `store` directly. Because `redux/store.ts` itself eagerly imports
 * `rootReducer.ts`, which in turn eagerly imports those same slice files to
 * pull out their reducers, the slice file would end up being evaluated twice
 * in CommonJS module land — and during the inner evaluation the reducer
 * exports were still undefined. `combineReducers` then silently dropped the
 * missing keys, which caused RTK 2.x's `slice.selectors.X(state)` to throw
 * "selectSlice returned undefined for an uninjected slice reducer" at runtime.
 *
 * By depending only on `redux`'s type, this file pulls in nothing else from
 * the app, so importing it from a slice file no longer creates a cycle.
 *
 * `redux/store.ts` registers the store via `setStore()` immediately after
 * creating it.
 *
 * ── Dependent facades ──
 * The following store facades use getStore() from this module:
 *   - mySongsPage.store.ts
 *   - mySongsLibrary.store.ts
 *   - mySongsFiltersStore.ts
 *   - musicHomeStore.ts
 *
 * ── Test / Storybook troubleshooting ──
 * If a test or story that imports one of these facades throws
 *   "Redux store accessed before initialization…"
 * it means redux/store.ts was never evaluated (and therefore setStore() never
 * ran). Two remedies:
 *   1. Wrap the component under test in <ReduxProvider> (preferred — this
 *      transitively imports redux/store.ts).
 *   2. Add a bare side-effect import at the top of the test file:
 *        import "<relative-path>/redux/store";
 *
 * A guard test in __tests__/storeImportGuard.test.ts prevents new production
 * files from importing redux/store directly.
 */
// The configured store includes the thunk middleware (see redux/store.ts), so
// callers must be able to dispatch thunk action creators in addition to plain
// actions. We avoid importing the actual store type from redux/store.ts so that
// this module can be safely imported from slice-store files without recreating
// the circular dependency it was designed to break.
type ThunkAwareStore = Omit<Store<AppState>, "dispatch"> & {
  dispatch: ThunkDispatch<AppState, unknown, Action>;
};

let _store: ThunkAwareStore | undefined;

export const setStore = (store: Store): void => {
  _store = store as ThunkAwareStore;
};

export const getStore = (): ThunkAwareStore => {
  if (!_store) {
    throw new Error(
      "Redux store accessed before initialization. Ensure redux/store.ts has been imported."
    );
  }
  return _store;
};
